// Shared DOM tooltip (same surface as chart-segment-tooltip). Avoids Bootstrap
// Tooltip instances that can orphan on Turbo navigations.

let sharedTooltip = null
let activeAnchor = null

function escapeHtml(value) {
  return String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
}

// Turbo Drive replaces <body> on every page visit, so the node created here is
// detached while this module variable survives. Without the isConnected check we
// keep writing into an off-document node: the tooltip silently stops appearing
// until the next full reload.
function ensureTooltip() {
  if (sharedTooltip?.isConnected) return sharedTooltip

  // Only ever one live node, and it is ours: anything already in the document
  // is a copy carried in by a snapshot, so it would just sit there stuck.
  document.querySelectorAll(".chart-segment-tooltip").forEach((node) => node.remove())

  sharedTooltip = document.createElement("div")
  sharedTooltip.className = "chart-segment-tooltip"
  sharedTooltip.setAttribute("role", "tooltip")
  document.body.appendChild(sharedTooltip)

  return sharedTooltip
}

// The node lives in <body>, so Turbo would copy it into the page snapshot and
// every restoration visit (back/forward) would bring back one more orphan.
function destroyTooltip() {
  activeAnchor = null
  sharedTooltip?.remove()
  sharedTooltip = null
}

document.addEventListener("turbo:before-cache", destroyTooltip)

// Single owner for the shared node: callers that render their own markup still
// go through here so the node and the active anchor stay in sync.
export function claimFloatingTooltip(anchor) {
  const tooltip = ensureTooltip()
  activeAnchor = anchor ?? null

  return tooltip
}

function computePosition(anchorRect, tooltipRect, placement, gap = 8) {

  if (placement === "right") {
    let left = anchorRect.right + gap
    let top = anchorRect.top + anchorRect.height / 2 - tooltipRect.height / 2

    if (left + tooltipRect.width > window.innerWidth - gap) {
      left = anchorRect.left - tooltipRect.width - gap
    }

    left = Math.max(gap, Math.min(left, window.innerWidth - tooltipRect.width - gap))
    top = Math.max(gap, Math.min(top, window.innerHeight - tooltipRect.height - gap))

    return { left, top }
  }

  let left = anchorRect.left + anchorRect.width / 2 - tooltipRect.width / 2
  let top = anchorRect.top - tooltipRect.height - gap

  if (top < gap) top = anchorRect.bottom + gap

  left = Math.max(gap, Math.min(left, window.innerWidth - tooltipRect.width - gap))
  top = Math.max(gap, Math.min(top, window.innerHeight - tooltipRect.height - gap))

  return { left, top }
}

export function showFloatingTooltip(anchor, text, { placement = "right" } = {}) {
  if (!anchor || !text) return

  showFloatingTooltipHtml(
    anchor,
    `<div class="chart-segment-tooltip__label">${escapeHtml(text)}</div>`,
    { placement },
  )
}

export function showFloatingTooltipHtml(anchor, html, { placement = "right", interactive = false, gap } = {}) {
  if (!anchor || !html) return

  const tooltip = claimFloatingTooltip(anchor)

  tooltip.classList.toggle("chart-segment-tooltip--interactive", interactive)

  tooltip.innerHTML = html
  tooltip.classList.remove("is-visible")
  tooltip.style.display = "block"
  tooltip.style.visibility = "hidden"
  tooltip.style.opacity = "0"
  tooltip.style.left = "-9999px"
  tooltip.style.top = "-9999px"
  tooltip.style.transform = "none"

  const tooltipRect = tooltip.getBoundingClientRect()
  const anchorRect = anchor.getBoundingClientRect()
  const resolvedGap = gap ?? (interactive ? 2 : 8)
  const { left, top } = computePosition(anchorRect, tooltipRect, placement, resolvedGap)

  tooltip.style.left = `${left}px`
  tooltip.style.top = `${top}px`
  tooltip.style.visibility = ""
  tooltip.style.opacity = ""
  tooltip.classList.add("is-visible")
}

export function hideFloatingTooltip({ force = false } = {}) {
  if (!force && !activeAnchor) return

  if (sharedTooltip) {
    sharedTooltip.classList.remove("is-visible", "chart-segment-tooltip--interactive")
    sharedTooltip.style.visibility = "hidden"
    sharedTooltip.style.opacity = "0"
  }

  activeAnchor = null
}

export function hideFloatingTooltipForAnchor(anchor) {
  if (activeAnchor === anchor) hideFloatingTooltip({ force: true })
}

export function floatingTooltipElement() {
  return sharedTooltip?.isConnected ? sharedTooltip : null
}

export function isPointerOverFloatingTooltip(clientX, clientY) {
  const tooltip = floatingTooltipElement()
  if (!tooltip?.classList.contains("is-visible")) return false

  const rect = tooltip.getBoundingClientRect()
  return (
    clientX >= rect.left &&
    clientX <= rect.right &&
    clientY >= rect.top &&
    clientY <= rect.bottom
  )
}
